"use client";

import { ArrowDownUpIcon, SearchIcon, XIcon } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import { BlogCard, type BlogCardPost } from "./blog-card";

export interface BlogTab {
  label: string;
  href: string;
}

/**
 * Category tabs over a searchable, sortable grid of posts. Tabs are links, so
 * each category keeps its own URL; search and sort only reorder what the
 * current tab already shows. Tab links keep the scroll position: every tab
 * renders the same hero above this bar, so only the grid below it changes.
 *
 * `children` replaces the post grid for a tab that lists something else
 * (Press), and hides search and sort, which only apply to posts.
 */
export function BlogGrid({
  posts,
  searchPosts = posts,
  tabs,
  activeHref,
  children,
}: {
  posts: BlogCardPost[];
  /** What a search runs over; /blog passes its hero post in here too. */
  searchPosts?: BlogCardPost[];
  tabs: BlogTab[];
  activeHref: string;
  children?: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(false);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? searchPosts.filter((p) =>
          `${p.title} ${p.description} ${p.author}`.toLowerCase().includes(q),
        )
      : posts;
    return oldestFirst ? [...filtered].reverse() : filtered;
  }, [posts, searchPosts, query, oldestFirst]);

  return (
    <div className="flex flex-col gap-10">
      <div className="border-marketing-border flex items-center justify-between gap-4 border-b pb-3">
        {searching ? (
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder="Search posts"
            aria-label="Search posts"
            className="text-marketing-text placeholder:text-marketing-text-muted w-full bg-transparent text-[15px] outline-none [&::-webkit-search-cancel-button]:appearance-none"
          />
        ) : (
          <nav
            aria-label="Blog categories"
            className="flex flex-wrap gap-x-5 gap-y-2"
          >
            {tabs.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                scroll={false}
                aria-current={tab.href === activeHref ? "page" : undefined}
                className={cn(
                  "text-[15px] transition-colors",
                  tab.href === activeHref
                    ? "text-marketing-text"
                    : "text-marketing-text-muted hover:text-marketing-text",
                )}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
        )}
        <div
          className={cn(
            "flex shrink-0 items-center gap-1",
            children && "hidden",
          )}
        >
          <button
            type="button"
            aria-label={searching ? "Close search" : "Search posts"}
            onClick={() => {
              setSearching(!searching);
              setQuery("");
            }}
            className="text-marketing-text-muted hover:text-marketing-text hover:bg-marketing-cream inline-flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors"
          >
            {searching ? (
              <XIcon className="size-4" aria-hidden="true" />
            ) : (
              <SearchIcon className="size-4" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            aria-label={oldestFirst ? "Show newest first" : "Show oldest first"}
            aria-pressed={oldestFirst}
            onClick={() => {
              setOldestFirst(!oldestFirst);
            }}
            className="text-marketing-text-muted hover:text-marketing-text hover:bg-marketing-cream inline-flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors"
          >
            <ArrowDownUpIcon className="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {children ??
        (shown.length > 0 ? (
          <ul className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((post) => (
              <li key={post.slug} className="flex">
                <BlogCard post={post} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-marketing-text-muted py-12 text-center text-[15px]">
            No posts match &ldquo;{query}&rdquo;.
          </p>
        ))}
    </div>
  );
}
